import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiCheckCircle, HiSignal, HiOutlineCpuChip, HiPhone, HiGlobeAsiaAustralia, HiCheck, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { saveEnquiry } from '../data/enquiriesData';
export default function TelecomServices() {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', company: '', region: '', serviceNeeded: 'SLA Power Maintenance', message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    setIsSubmitted(true);

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      productId: '',
      productName: `Telecom Service: ${formData.serviceNeeded}`,
      city: `${formData.company} (${formData.region})`,
      message: formData.message,
      type: 'telecom',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({
        title: 'Telecom Enquiry Logged!',
        text: `Thank you, ${formData.name}. We have registered your request for "${formData.serviceNeeded}". Our operations coordinator will call you back within 2 hours.`,
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      setFormData({ name: '', phone: '', email: '', company: '', region: '', serviceNeeded: 'SLA Power Maintenance', message: '' });
      e.target.reset();
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    } finally {
      setIsSubmitted(false);
    }
  };

  const workflowSteps = [
    { icon: <HiOutlineCpuChip className="w-6 h-6" />, title: '1. Remote Telemetry', desc: 'NOC monitors live fuel logs, run hours, grid failures, battery cycles, and temperature spikes.' },
    { icon: <HiSignal className="w-6 h-6" />, title: '2. Dynamic Routing', desc: 'Alert triggers auto-assignment of the closest cluster technician based on real-time GPS locations.' },
    { icon: <HiGlobeAsiaAustralia className="w-6 h-6" />, title: '3. Site Resolution', desc: 'Engineer arrives under SLA limits with replacement parts to swap, repair, or refuel the genset backup.' },
    { icon: <HiCheck className="w-6 h-6" />, title: '4. Quality Audit', desc: 'Parameters verified, load test completed, digital signature saved, and NOC ticket closed.', accent: true },
  ];

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Telecom service.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">NOC Operations</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Telecom <span className="text-gradient-gold">Services</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Directly managed SLA power maintenance, preventive backup care, and diesel fueling administration for cellular tower hubs nationwide.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <span>Home</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Telecom Services</span>
        </div>
      </div>

      {/* Section 1: Core Capabilities */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="icon-box card-icon mb-6">
              <HiSignal className="w-6 h-6" />
            </div>
            <span className="eyebrow">Network Resilience</span>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-6">
              Turnkey Managed Power & Tower Infrastructure SLA Maintenance
            </h2>
            <div className="space-y-3 text-sm text-body leading-relaxed mb-8">
              <p>Telecom towers operate under extreme uptime expectations. Cellular carriers require constant, stable backup configurations to prevent call drops and network outages. Green Gusto Energy Pvt. Ltd. acts as a strategic operations partner, running continuous maintenance and fueling operations for thousands of tower sites across regional circles.</p>
              <p>We employ localized cluster engineering teams. Each cluster consists of a dedicated service vehicle stocked with essential spares (AVRs, fuel filters, gaskets, belts) and direct radio links to our 24/7 National Operations Center (NOC).</p>
            </div>
            <div className="space-y-3">
              {[
                '99.99% Network Power Uptime Guarantees (SLA-driven execution)',
                'Comprehensive cluster coverage with localized mechanics and dispatch trucks',
                'IoT monitoring system integrations for instant voltage and oil drop alerts',
              ].map((pt, i) => (
                <div key={i} className="flex items-start gap-3">
                  <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-semibold text-heading">{pt}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Operations Dashboard Card */}
          <div className="lg:col-span-5 bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-premium relative overflow-hidden group hover:border-primary-200 transition-all duration-300">
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-accent/8 rounded-full blur-2xl" />
            <h3 className="font-display font-bold text-xs text-accent uppercase tracking-widest mb-7 relative z-10">Operations Dashboard</h3>
            <div className="space-y-4 relative z-10">
              {[
                { label: 'Total Telecom Sites Managed', value: '800+ Nodes' },
                { label: 'Guaranteed Dispatch SLA', value: '< 15 Mins Alert to Dispatch' },
                { label: 'Regional Circles Covered', value: 'Maharashtra & Goa' },
                { label: 'Fuel Compliance Audits', value: 'Weekly Logging & Checks' },
              ].map((row, i) => (
                <div key={i} className={`flex justify-between py-3 text-xs ${i < 3 ? 'border-b border-white/10' : ''}`}>
                  <span className="text-white/60 font-medium">{row.label}</span>
                  <span className="font-bold text-white">{row.value}</span>
                </div>
              ))}
            </div>
            <Link to="/contact#enquiry" className="btn-accent w-full justify-center mt-8 py-3 rounded-xl text-xs relative z-10">
              Consult NOC Planner
              <HiArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2: Workflow */}
      <section className="text-heading py-24 border-t border-b border-border bg-gradient-to-b from-[#F0F5F2] to-[#F8FAF9] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-light/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="text-center mb-16">
            <span className="eyebrow">Process Logistics</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">How We Execute 99.99% Network Uptime</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            {workflowSteps.map((step, i) => (
              <div key={i} className={`rounded-2xl p-6 border bg-white border-border shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-5 ${step.accent ? 'bg-accent text-white' : 'bg-primary text-white'}`}>
                  {step.icon}
                </div>
                <h4 className="text-xs font-bold text-heading uppercase tracking-wide mb-3 group-hover:text-primary transition-colors">{step.title}</h4>
                <p className="text-xs text-body leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: SLA Parameters + Cluster Management */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          {/* SLA Table */}
          <div className="lg:col-span-5 bg-white border border-border rounded-2xl overflow-hidden shadow-card">
            <div className="px-6 py-4 bg-primary border-b border-primary-dark">
              <h3 className="font-display font-bold text-xs text-white uppercase tracking-wider">Telecom SLA Parameters</h3>
            </div>
            <div className="divide-y divide-border">
              {[
                { label: 'Uptime Commitment', val: '99.99%' },
                { label: 'Emergency Dispatch SLA', val: '< 10 Mins Dispatch' },
                { label: 'On-Site Arrival Target', val: '< 2 Hours' },
                { label: 'NOC Operation Cycle', val: '24 × 7 × 365 Days' },
              ].map((row, i) => (
                <div key={i} className="flex justify-between px-6 py-4 text-sm">
                  <span className="text-body">{row.label}</span>
                  <span className="font-bold text-primary">{row.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cluster Management */}
          <div className="lg:col-span-7">
            <span className="eyebrow">Corporate Trust</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-6">
              Directly Employed Logistics & Localized Cluster Management
            </h2>
            <p className="text-sm text-body leading-relaxed mb-8">
              Unlike aggregators who delegate services to local mechanics, Green Gusto maintains direct operations control. Our engineers are certified and operate on strict response guidelines. We own our spare parts logistics and maintain regional storage yards. This allows tower operators to depend on us for long-term SLAs.
            </p>
            <a href="tel:+919609600600" className="inline-flex items-center gap-3 px-7 py-4 bg-primary hover:bg-primary-dark text-white font-bold text-sm rounded-xl transition-all shadow-premium hover:shadow-premium-hover hover:-translate-y-0.5">
              <HiPhone className="w-4.5 h-4.5 text-accent" />
              Call Telecom Division
            </a>
          </div>
        </div>

        {/* Enquiry Form */}
        <div id="telecom-enquiry-form" className="mt-20 pt-10 border-t border-border/40 w-full">
          <div className="text-center mb-10">
            <span className="eyebrow">Direct NOC Quotation</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Request SLA Service Consultation</h2>
            <p className="text-sm text-body-muted max-w-xl mx-auto mt-2">
              Complete the telecom circle details below to request a tailored SLA proposal from our Operations team.
            </p>
          </div>

          <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 md:p-10 shadow-premium relative overflow-hidden max-w-5xl mx-auto">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
            <div className="relative z-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Full Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Contact Number</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="e.g. +91 98765 43210" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Email Address</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="name@company.com" className="input-dark" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Operator / Company Name</label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} required placeholder="e.g. Reliance Jio" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Telecom Circle / Region</label>
                    <input type="text" name="region" value={formData.region} onChange={handleChange} required placeholder="e.g. Maharashtra Circle" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Service Needed</label>
                    <select name="serviceNeeded" value={formData.serviceNeeded} onChange={handleChange} required className="input-dark bg-charcoal text-white/85 appearance-none w-full">
                      <option value="SLA Power Maintenance" className="bg-white text-heading">SLA Power Maintenance</option>
                      <option value="Diesel Fueling Administration" className="bg-white text-heading">Diesel Fueling Administration</option>
                      <option value="Preventive Backup Care" className="bg-white text-heading">Preventive Backup Care</option>
                      <option value="Cluster Audit & Verification" className="bg-white text-heading">Cluster Audit & Verification</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                  <div className="md:col-span-8">
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message Details</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={2} required placeholder="Cluster sizes, SLA targets, fueling schedules..." className="input-dark resize-none" />
                  </div>
                  <div className="md:col-span-4">
                    <button type="submit" disabled={isSubmitted} className="btn-green rounded-xl w-full justify-center py-3.5 text-sm disabled:opacity-60">
                      Submit Request
                      <HiArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
